import classNames from 'classnames';
import { useState, useCallback, useMemo } from 'react';

import { Button, Grid, GridItem, Text } from '@farmersdog/corgi-x';

import { LightboxId, useGlobalLightbox } from '../../../hooks';
import { useFetchQuote } from '../../../hooks/useFetchQuote';
import { useVetCustomizedPlan } from '../../../hooks/useVetCustomizedPlan';
import { useCheckVetRecipesAvailability } from '../../../hooks/useVetCustomizedPlan/useCheckVetRecipesAvailability';
import RecipeModal from '../../RecipeModal/RecipeModal';
import { useCheapestQuotes } from '../hooks';
import { getQuoteData } from '../utils/getQuoteData';

import { PriceDivider } from './PriceDivider';
import { RecipeCardList } from './RecipeCardList';
import styles from './RecipesSelection.module.css';
import { SubmitButton } from './SubmitButton';
import { TopSection } from './TopSection';
import { VetTopSection } from './VetTopSection';

import type { FetchLeadQuery, FetchQuoteQuery } from '../../../graphql/types';
import type { PetRecipes } from '../../../types';

export interface RecipesSelectionProps {
  returnToRecommendedPlan: () => void;
  currentPetRecipeSelection: string[];
  onSubmit: (quote: FetchQuoteQuery['fetchQuote'] | null) => void;
  isFormSubmitting: boolean;
  onChangeSelection: (recipe: string) => void;
  petName: string;
  pets: FetchLeadQuery['lead']['pets'];
  availablePetRecipesMap: PetRecipes;
  isEligibleForDefaultPlanFrequencyExperiment: boolean;
}

export const MAX_SELECTED_RECIPES = 3;

export function RecipesSelection({
  returnToRecommendedPlan,
  currentPetRecipeSelection,
  onSubmit,
  isFormSubmitting,
  onChangeSelection,
  petName,
  pets,
  availablePetRecipesMap,
  isEligibleForDefaultPlanFrequencyExperiment,
}: RecipesSelectionProps) {
  const recipes = availablePetRecipesMap[petName];

  const { open: openRecipeDetailModal } = useGlobalLightbox({
    id: LightboxId.RecipeDetailModal,
  });

  const [selectedRecipeForDetailsModal, setSelectedRecipeForDetailsModal] =
    useState<string>();

  const onClickSeeDetails = useCallback(
    (recipe: string) => {
      setSelectedRecipeForDetailsModal(recipe);
      openRecipeDetailModal();
    },
    [openRecipeDetailModal]
  );

  const vetCustomizedPlan = useVetCustomizedPlan();
  const isVetCustomizedPlan = Boolean(vetCustomizedPlan);
  const isVetCustomizedPlanForCurrentPet =
    isVetCustomizedPlan && petName === vetCustomizedPlan?.dogName;

  const freshVetRecipeNames = useMemo(
    () =>
      isVetCustomizedPlanForCurrentPet
        ? (vetCustomizedPlan?.freshRecipeNames ?? [])
        : [],
    [isVetCustomizedPlanForCurrentPet, vetCustomizedPlan]
  );

  const availableRecipeNames = useMemo(
    () => recipes.map(recipe => recipe.name),
    [recipes]
  );

  useCheckVetRecipesAvailability({
    vetRecipeNames: freshVetRecipeNames,
    availableRecipeNames: availableRecipeNames,
  });

  const { cheapestCoreQuote, cheapestLpfQuote } = useCheapestQuotes({
    currentPetName: petName,
    availablePetRecipesMap,
    pets,
  });

  const { quote, quoteQueryState } = useFetchQuote({
    currentPetName: petName,
    availablePetRecipesMap,
    currentPetRecipeSelection,
    pets,
  });

  const { petDailySubtotal, petDailyDiscountedSubtotal, discountPercentage } =
    getQuoteData({
      quote,
      currentPetName: petName,
    });

  const { petDailySubtotal: cheapestCorePetDailySubtotal } = getQuoteData({
    quote: cheapestCoreQuote,
    currentPetName: petName,
  });
  const { petDailySubtotal: cheapestLpfPetDailySubtotal } = getQuoteData({
    quote: cheapestLpfQuote,
    currentPetName: petName,
  });

  const lpfRecipes = useMemo(
    () => recipes.filter(recipe => recipe.content.productLine === 'lpf'),
    [recipes]
  );

  const nonLpfRecipes = useMemo(
    () => recipes.filter(recipe => recipe.content.productLine !== 'lpf'),
    [recipes]
  );

  const areMoreThanOneLpfRecipes = lpfRecipes.length > 1;
  const recipeForDisplayInDetailsModal = recipes.find(
    recipe => recipe.name === selectedRecipeForDetailsModal
  );

  const recommendedRecipes = recipes.filter(recipe => recipe.recommended);
  const hasMultipleRecommendedRecipes = recommendedRecipes.length > 1;
  const hasAtLeastOneRecommendedRecipe = recommendedRecipes.length > 0;
  const onChangeSelectionForRecipeCardList = isVetCustomizedPlanForCurrentPet
    ? undefined
    : onChangeSelection;

  const handleSubmit = useCallback(() => {
    onSubmit(quote);
  }, [onSubmit, quote]);

  return (
    <>
      {recipeForDisplayInDetailsModal && (
        <RecipeModal
          recipe={recipeForDisplayInDetailsModal}
          petName={petName}
        />
      )}
      <Grid
        justifyContent="flex-start"
        alignItems="center"
        flexDirection="column"
        vSpacing="lg"
        data-testid="recipes-selection-container"
        className={classNames(styles.recipesBackgroundContainer, {
          [styles.experimentalBackground]:
            isEligibleForDefaultPlanFrequencyExperiment,
        })}
      >
        {isVetCustomizedPlanForCurrentPet ? (
          <VetTopSection
            petName={vetCustomizedPlan.dogName}
            vetName={vetCustomizedPlan.vetName}
            freshRecipeNames={vetCustomizedPlan.freshRecipeNames ?? []}
          />
        ) : (
          <TopSection
            petName={petName}
            hasMultipleRecommendedRecipes={hasMultipleRecommendedRecipes}
          />
        )}

        {lpfRecipes.length > 0 && !isVetCustomizedPlanForCurrentPet ? (
          <Grid
            className={styles.gridContainer}
            columnGap={areMoreThanOneLpfRecipes ? 'lg' : 'xs'}
          >
            <GridItem
              className={styles.gridItem}
              xl={areMoreThanOneLpfRecipes ? 5 : 3}
            >
              <div
                data-testid="lpf-recipes-container"
                className={
                  areMoreThanOneLpfRecipes
                    ? styles.dividerWrapper
                    : styles.oneRecipeDividerWrapper
                }
              >
                <PriceDivider
                  text={`Starting at ${cheapestLpfPetDailySubtotal}/day`}
                />
              </div>
              <Grid
                columnGap="md"
                className={styles.recipeOptionsContainer}
                justifyContent={{ xs: 'flex-start', md: 'center' }}
              >
                <RecipeCardList
                  recipes={lpfRecipes}
                  onChangeSelection={onChangeSelectionForRecipeCardList}
                  currentPetRecipeSelection={currentPetRecipeSelection}
                  onClickSeeDetails={onClickSeeDetails}
                  isVetCustomizedPlanForCurrentPet={
                    isVetCustomizedPlanForCurrentPet
                  }
                  vetCustomizedPlan={vetCustomizedPlan}
                />
              </Grid>
            </GridItem>
            <GridItem
              className={styles.gridItem}
              xl={areMoreThanOneLpfRecipes ? 7 : 9}
            >
              <PriceDivider
                text={`Starting at ${cheapestCorePetDailySubtotal}/day`}
              />

              <Grid
                justifyContent={{
                  md: 'center',
                  xl: 'flex-start',
                }}
                columnGap="md"
                className={styles.recipeOptionsContainer}
              >
                <RecipeCardList
                  recipes={nonLpfRecipes}
                  onChangeSelection={onChangeSelectionForRecipeCardList}
                  currentPetRecipeSelection={currentPetRecipeSelection}
                  onClickSeeDetails={onClickSeeDetails}
                  isVetCustomizedPlanForCurrentPet={
                    isVetCustomizedPlanForCurrentPet
                  }
                  vetCustomizedPlan={vetCustomizedPlan}
                />
              </Grid>
            </GridItem>
          </Grid>
        ) : (
          <Grid
            columnGap="sm"
            className={styles.recipeOptionsContainer}
            justifyContent="center"
          >
            <RecipeCardList
              recipes={recipes}
              onChangeSelection={onChangeSelectionForRecipeCardList}
              currentPetRecipeSelection={currentPetRecipeSelection}
              onClickSeeDetails={onClickSeeDetails}
              isVetCustomizedPlanForCurrentPet={
                isVetCustomizedPlanForCurrentPet
              }
              vetCustomizedPlan={vetCustomizedPlan}
            />
          </Grid>
        )}

        {hasAtLeastOneRecommendedRecipe ? (
          <GridItem md={12} justifyContent="center" alignItems={'center'}>
            <Button
              type="button"
              variant="plain-text"
              onClick={returnToRecommendedPlan}
              className={styles.returnButton}
            >
              <Text as="h3" variant="heading-16" color="carrot-2">
                Back to Recommended Plan
              </Text>
            </Button>
          </GridItem>
        ) : (
          <Grid bottomSpacing={'xxl'}>{null}</Grid> // empty spacing for lack of back to recommended plan button
        )}
        <SubmitButton
          hasQuote={!!quote}
          currentSelection={currentPetRecipeSelection}
          isQuoteLoading={quoteQueryState.loading}
          hasSelectedRecipes={Boolean(currentPetRecipeSelection?.length > 0)}
          onClick={handleSubmit}
          formSubmitting={isFormSubmitting}
          petDailySubtotal={petDailySubtotal}
          petDailyDiscountedSubtotal={petDailyDiscountedSubtotal}
          discountPercentage={discountPercentage}
          trialDailyPriceLabel={
            isEligibleForDefaultPlanFrequencyExperiment
              ? 'Trial Period'
              : 'First Box'
          }
        />
      </Grid>
    </>
  );
}
